// src/pages/Buyer/Shipping.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  LinearProgress,
  Link,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  LocalShipping,
  LocationOn,
  CalendarToday,
  AccessTime,
  Download,
  Refresh,
  ArrowBack,
  CheckCircle,
  Schedule,
  CheckCircleOutline,
  RadioButtonUnchecked,
  ErrorOutline,
  WatchLater,
  DoneAll,
  FileCopy,
  Chat,
  Support,
  Timeline,
  Notifications,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { shippingAPI, enquiryAPI } from '../../services/api';
import { formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerShipping = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();
  
  const [shipmentData, setShipmentData] = useState(null);
  const [enquiryData, setEnquiryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);

  useEffect(() => {
    fetchShippingData();
    fetchEnquiryData();
  }, [enquiryId]);

  const fetchShippingData = async () => {
    try {
      setLoading(true);
      const response = await shippingAPI.getShipment(enquiryId);
      const data = response.data;
      setShipmentData(data);
      
      // Generate timeline events from shipment data
      generateTimelineEvents(data);
    } catch (error) {
      console.error('Error fetching shipping data:', error);
      toast.error('Failed to load shipping information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiryData = async () => {
    try {
      const response = await enquiryAPI.getById(enquiryId);
      setEnquiryData(response.data);
    } catch (error) {
      console.error('Error fetching enquiry data:', error);
    }
  };

  const generateTimelineEvents = (data) => {
    const events = [];
    
    if (data.createdAt) {
      events.push({
        id: 1,
        title: 'Shipment Created',
        description: 'Shipping order initiated',
        timestamp: data.createdAt,
        icon: <CheckCircleOutline />,
        color: '#4caf50',
      });
    }
    
    if (data.container_assigned_date) {
      events.push({
        id: 2,
        title: 'Container Assigned',
        description: `Container #${data.container_number} assigned`,
        timestamp: data.container_assigned_date,
        icon: <LocalShipping />,
        color: '#2196f3',
      });
    }
    
    if (data.actual_departure) {
      events.push({
        id: 3,
        title: 'Departed Origin',
        description: `Vessel ${data.vessel_name} departed`,
        timestamp: data.actual_departure,
        icon: <LocationOn />,
        color: '#ff9800',
      });
    }
    
    if (data.actual_arrival) {
      events.push({
        id: 4,
        title: 'Arrived Destination',
        description: 'Arrived at destination port',
        timestamp: data.actual_arrival,
        icon: <CheckCircle />,
        color: '#4caf50',
      });
    }
    
    if (data.customs_cleared_date) {
      events.push({
        id: 5,
        title: 'Customs Cleared',
        description: 'Customs clearance completed',
        timestamp: data.customs_cleared_date,
        icon: <DoneAll />,
        color: '#4caf50',
      });
    }
    
    if (data.delivery_date) {
      events.push({
        id: 6,
        title: 'Delivered',
        description: 'Shipment delivered successfully',
        timestamp: data.delivery_date,
        icon: <CheckCircle />,
        color: '#4caf50',
      });
    }
    
    // Sort by timestamp
    events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    setTimelineEvents(events);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchShippingData();
      toast.success('Shipping information updated');
    } catch (error) {
      console.error('Error refreshing shipping data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const getShippingSteps = () => {
    if (!shipmentData) return [];
    
    const steps = [
      { label: 'Order Confirmed', status: 'confirmed', description: 'Payment verified, order confirmed' },
      { label: 'Container Assigned', status: 'assigned', description: 'Container number assigned' },
      { label: 'Port Loading', status: 'loading', description: 'Loading at port of origin' },
      { label: 'In Transit', status: 'in_transit', description: 'Shipment in transit' },
      { label: 'Port Arrival', status: 'arrived', description: 'Arrived at destination port' },
      { label: 'Delivered', status: 'delivered', description: 'Delivered to consignee' },
    ];
    
    const currentStatus = shipmentData.status;
    const statusOrder = ['confirmed', 'assigned', 'loading', 'in_transit', 'arrived', 'delivered'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex,
    }));
  };

  const getProgressValue = () => {
    if (!shipmentData) return 0;
    
    const statusOrder = ['confirmed', 'assigned', 'loading', 'in_transit', 'arrived', 'delivered'];
    const currentIndex = statusOrder.indexOf(shipmentData.status);
    return ((currentIndex + 1) / statusOrder.length) * 100;
  };

  const downloadDocument = (type) => {
    // Implement document download functionality
    toast.success(`Downloading ${type}...`);
    // In real implementation, you would call an API endpoint
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          Loading shipping information...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Please wait while we fetch the latest tracking details
        </Typography>
      </Container>
    );
  }

  const shippingSteps = getShippingSteps();
  const progressValue = getProgressValue();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/buyer/enquiries')}
          variant="outlined"
          sx={{ mb: 3 }}
        >
          Back to Enquiries
        </Button>
        
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Shipping Tracking
              </Typography>
              <Typography variant="body1">
                Enquiry #{enquiryId} • {enquiryData?.productName || 'Fruit Shipment'}
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Status'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {!shipmentData || shipmentData.message === 'No shipment found' ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Timeline sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Shipping Not Yet Initiated
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your shipment details will appear here once the order is confirmed and shipping begins.
            Please check back later or contact our support team for updates.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/buyer/enquiries')}
            >
              View Enquiries
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/support')}
              startIcon={<Support />}
            >
              Contact Support
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          {/* Progress Tracker */}
          <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Shipment Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estimated delivery: {formatDate(shipmentData.estimated_delivery)}
                </Typography>
              </Box>
              <Chip
                label={shipmentData.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(shipmentData.status)}
                size="medium"
                icon={shipmentData.status === 'delivered' ? <CheckCircle /> : <WatchLater />}
                sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
              />
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={progressValue} 
              sx={{ 
                height: 10, 
                borderRadius: 5, 
                mb: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                }
              }}
              color={getStatusColor(shipmentData.status)}
            />
            
            <Stepper alternativeLabel sx={{ mb: 2 }}>
              {shippingSteps.map((step, index) => (
                <Step key={step.label} completed={step.completed} active={step.active}>
                  <StepLabel 
                    StepIconProps={{
                      icon: step.completed ? <CheckCircle /> : index + 1,
                    }}
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontWeight: step.active ? 'bold' : 'normal',
                      }
                    }}
                  >
                    {step.label}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Grid container spacing={3}>
            {/* Shipment Details */}
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocalShipping sx={{ color: 'primary.main' }} /> Shipment Details
                </Typography>
                
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalShipping fontSize="small" />
                            Container Number
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Chip 
                            label={shipmentData.container_number || 'Not assigned'} 
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          Vessel Name
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          {shipmentData.vessel_name || 'Not assigned'}
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          Bill of Lading (BL) Number
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={shipmentData.bl_number || 'Not issued'} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {shipmentData.bl_number && (
                              <Button
                                size="small"
                                startIcon={<FileCopy />}
                                onClick={() => downloadDocument('BL')}
                              >
                                Copy
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn fontSize="small" />
                            Port of Loading
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          {shipmentData.port_of_loading || 'Not specified'}
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn fontSize="small" />
                            Port of Discharge
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          {shipmentData.port_of_discharge || 'Not specified'}
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" />
                            Expected Departure
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          {formatDate(shipmentData.expected_departure)}
                        </TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" />
                            Expected Arrival
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          {formatDate(shipmentData.expected_arrival)}
                        </TableCell>
                      </TableRow>
                      
                      {shipmentData.actual_departure && (
                        <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime fontSize="small" />
                              Actual Departure
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Chip 
                              label={formatDateTime(shipmentData.actual_departure)} 
                              size="small"
                              color="success"
                              icon={<CheckCircle />}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {shipmentData.actual_arrival && (
                        <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime fontSize="small" />
                              Actual Arrival
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Chip 
                              label={formatDateTime(shipmentData.actual_arrival)} 
                              size="small"
                              color="success"
                              icon={<CheckCircle />}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {shipmentData.tracking_url && (
                        <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell component="th" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                            Live Tracking
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Button
                              variant="contained"
                              size="small"
                              href={shipmentData.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<LocalShipping />}
                            >
                              Track on Shipping Line
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Timeline Section */}
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Timeline sx={{ color: 'primary.main' }} /> Shipment Timeline
                </Typography>
                
                <List>
                  {timelineEvents.map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: event.color }}>
                            {event.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight="medium">
                              {event.title}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {event.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(event.timestamp)}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      {index < timelineEvents.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            {/* Sidebar - Quick Actions & Info */}
            <Grid item xs={12} lg={4}>
              {/* Quick Actions Card */}
              <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Notifications sx={{ color: 'primary.main' }} /> Quick Actions
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {shipmentData.bl_number && (
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        fullWidth
                        onClick={() => downloadDocument('Bill of Lading')}
                        size="large"
                      >
                        Download BL Copy
                      </Button>
                    )}
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Chat />}
                      onClick={() => navigate(`/chat/${enquiryId}`)}
                      size="large"
                    >
                      Contact Shipping Team
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Support />}
                      onClick={() => window.location.href = '/support'}
                      size="large"
                    >
                      Support & Help Center
                    </Button>
                    
                    <Button
                      variant="text"
                      fullWidth
                      startIcon={<FileCopy />}
                      onClick={() => navigator.clipboard.writeText(window.location.href)}
                      size="large"
                    >
                      Copy Tracking Link
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              
              {/* Estimated Timeline Card */}
              <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ color: 'primary.main' }} /> Estimated Timeline
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        DEPARTURE
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(shipmentData.expected_departure) || 'To be scheduled'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        TRANSIT TIME
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {shipmentData.transit_days || '25-30'} days
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        ESTIMATED ARRIVAL
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" color="primary.main">
                        {formatDate(shipmentData.expected_arrival) || 'To be calculated'}
                      </Typography>
                    </Box>
                    
                    {shipmentData.actual_arrival && (
                      <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="success.dark">
                          DELIVERED ON
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.dark">
                          {formatDate(shipmentData.actual_arrival)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
              
              {/* Shipment Summary Card */}
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping sx={{ color: 'primary.main' }} /> Shipment Summary
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Shipment ID
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {shipmentData._id?.slice(-8) || 'N/A'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Enquiry ID
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {enquiryId}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Shipping Line
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {shipmentData.shipping_line || 'Maersk Line'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Container Type
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {shipmentData.container_type || '40ft Reefer'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Temperature Control
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {shipmentData.temperature || '2-4°C'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Additional Information & Support */}
          <Paper sx={{ p: 4, mt: 4, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Important Shipping Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tracking Updates
                  </Typography>
                  <Typography variant="body2">
                    • Container tracking updates every 24 hours
                    <br />
                    • Contact shipping line directly for real-time vessel tracking
                    <br />
                    • Email notifications for major status changes
                  </Typography>
                </Alert>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Customs Clearance
                  </Typography>
                  <Typography variant="body2">
                    • Typically takes 3-5 business days after arrival
                    <br />
                    • Required documents will be shared via email
                    <br />
                    • Local delivery arranged after clearance
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Alert severity="success">
                  <Typography variant="subtitle2" gutterBottom>
                    Need Help?
                  </Typography>
                  <Typography variant="body2">
                    For urgent shipping inquiries, contact:
                    <br />
                    • Email:{' '}
                    <Link href="mailto:shipping@fruitexport.com" fontWeight="medium">
                      shipping@fruitexport.com
                    </Link>
                    <br />
                    • Phone:{' '}
                    <Link href="tel:+15551234567" fontWeight="medium">
                      +1 (555) 123-4567
                    </Link>
                    <br />
                    • WhatsApp:{' '}
                    <Link href="https://wa.me/15551234567" fontWeight="medium">
                      +1 (555) 123-4567
                    </Link>
                  </Typography>
                </Alert>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Download Shipping Documents
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: 'white', color: 'primary.main', mt: 1 }}
                    startIcon={<Download />}
                  >
                    All Documents (ZIP)
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default BuyerShipping;